import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Image, KeyboardAvoidingView, Modal, Platform, Pressable, ScrollView, Text, TextInput, View } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import * as Crypto from 'expo-crypto';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '../../hooks/useTheme';
import { useSheetLayout } from '../../utilities/sheetLayout';
import { apiPost } from '../../utilities/apiClient';
import { getAppVersion, getPlatformDevice } from '../../utilities/devicePlatform';
import { captureFeedbackScreenshot, markScreenshot, type Screenshot, type Selection } from '../../services/feedback/screenshot';
import type { FeedbackOptions } from './FeedbackContext';
import ScreenshotPreview from './ScreenshotPreview';
import { makeStyles, sheetInsets } from './style';

const categories = [['problem', 'Problem'], ['idea', 'Idea'], ['praise', 'Something I like'], ['other', 'Other']] as const;
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

export default function FeedbackForm({ accountId, cognitoId, screen, initial, onClose, onSent }: {
  accountId: string; cognitoId: string; screen: string; initial: FeedbackOptions;
  onClose: () => void; onSent: () => void;
}) {
  const styles = makeStyles(useTheme());
  const sheet = useSheetLayout({ desktopMaxWidth: 540 });
  const insets = useSafeAreaInsets();
  const [category, setCategory] = useState('other');
  const [rating, setRating] = useState<number | undefined>(initial.rating);
  const [message, setMessage] = useState('');
  const [screenshot, setScreenshot] = useState<Screenshot | null>(null);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const mounted = useRef(true);
  const sending = useRef(false);
  const submissionId = useRef(Crypto.randomUUID());
  const input = useRef<TextInput>(null);
  const scroll = useRef<ScrollView>(null);
  useEffect(() => () => { mounted.current = false; }, []);
  useEffect(() => { submissionId.current = Crypto.randomUUID(); }, [category, rating, message, screenshot, selection]);

  const upload = async () => {
    setError('');
    try {
      const result = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ['images'], base64: true, quality: 0.8, allowsEditing: false });
      if (!mounted.current || result.canceled) return;
      const image = result.assets[0];
      if (!image.base64 || image.base64.length * 3 / 4 > MAX_IMAGE_BYTES) throw new Error('Choose a PNG or JPEG smaller than 2 MB.');
      // Native image-picker encodes its base64 output as JPEG; web preserves the chosen file type.
      const mime = Platform.OS === 'web' ? image.mimeType : 'image/jpeg';
      if (mime !== 'image/png' && mime !== 'image/jpeg') throw new Error('Choose a PNG or JPEG image.');
      const uri = `data:${mime};base64,${image.base64}`;
      const dimensions = await new Promise<{ width: number; height: number }>((resolve, reject) => Image.getSize(uri, (width, height) => resolve({ width, height }), reject));
      if (!mounted.current) return;
      setScreenshot({ uri, base64: image.base64, mime, ...dimensions });
      setSelection(null);
    } catch (err) { if (mounted.current) setError(err instanceof Error ? err.message : 'Could not open the image.'); }
  };
  const capture = async () => {
    setError('');
    setCapturing(true);
    try {
      const image = await captureFeedbackScreenshot();
      if (mounted.current) { setScreenshot(image); setSelection(null); }
    } catch (err) {
      if (mounted.current) setError((err as Error)?.name === 'NotAllowedError' ? 'Capture canceled. You can try again or upload an image.' : (err as Error)?.message || 'Could not capture the screen.');
    } finally { if (mounted.current) setCapturing(false); }
  };
  const send = async () => {
    if (sending.current) return;
    sending.current = true;
    setBusy(true);
    setError('');
    try {
      const image = screenshot ? await markScreenshot(screenshot, selection) : null;
      if (!mounted.current) return;
      const result = await apiPost(`/monitoring/feedback/${encodeURIComponent(accountId)}/${encodeURIComponent(cognitoId)}`, {
        submission_id: submissionId.current, category, rating, message: message.trim(),
        screenshot: image ? { base64: image.base64, mime: image.mime } : undefined,
        screen, platform: getPlatformDevice(), app_version: getAppVersion(), source: initial.source || 'manual',
      });
      if (!mounted.current) return;
      if (!result?.success || !result?.posted) throw new Error(result?.message || 'Feedback was not delivered. Please try again.');
      setScreenshot(null);
      setMessage('');
      setSent(true);
      onSent();
    } catch (err) { if (mounted.current) setError((err as Error)?.message || 'Feedback was not delivered. Please try again.'); }
    finally { sending.current = false; if (mounted.current) setBusy(false); }
  };
  const close = () => { if (!sending.current) onClose(); };
  // Unmount the modal immediately so its exit animation cannot enter the capture.
  if (capturing) return null;
  return (
    <Modal visible transparent animationType={sheet.animationType} onRequestClose={close} onShow={() => screenshot ? scroll.current?.scrollToEnd({ animated: false }) : input.current?.focus()}>
      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={[styles.overlay, sheet.overlayStyle]}>
        <View style={[styles.sheet, sheet.sheetStyle, sheetInsets(insets.bottom)]} accessibilityViewIsModal>
          <View style={styles.header}>
            <Text style={styles.title}>{sent ? 'Thank you' : 'Send feedback'}</Text>
            <Pressable accessibilityRole="button" accessibilityLabel="Close feedback" disabled={busy} onPress={close} style={styles.button}><Text style={styles.text}>Close</Text></Pressable>
          </View>
          {sent ? <>
            <Text accessibilityLiveRegion="polite" style={styles.text}>Your feedback reached the Eleplan team.</Text>
            <Pressable accessibilityRole="button" onPress={onClose} style={[styles.button, styles.primary]}><Text style={styles.primaryText}>Done</Text></Pressable>
          </> : <>
            <ScrollView ref={scroll} keyboardShouldPersistTaps="handled">
              <Text style={styles.muted}>Help us improve Eleplan. Share an idea, a problem, or something that worked well.</Text>
              <Text style={styles.label}>What is it about?</Text>
              <View style={styles.row}>{categories.map(([value, label]) => <Pressable key={value} accessibilityRole="radio" accessibilityState={{ checked: category === value }} disabled={busy} onPress={() => setCategory(value)} style={[styles.button, category === value && styles.selected]}><Text style={styles.text}>{label}</Text></Pressable>)}</View>
              <Text style={styles.label}>How is Eleplan working for you? (optional)</Text>
              <View style={styles.row}>{[1, 2, 3, 4, 5].map(value => <Pressable key={value} accessibilityRole="button" accessibilityLabel={`${value} out of 5 stars`} accessibilityState={{ selected: rating === value }} disabled={busy} onPress={() => setRating(rating === value ? undefined : value)} style={styles.button}><Text style={styles.stars}>{value <= (rating || 0) ? '★' : '☆'}</Text></Pressable>)}</View>
              <Text style={styles.label}>Your feedback</Text>
              <TextInput ref={input} accessibilityLabel="Your feedback" style={styles.input} multiline maxLength={2000} value={message} onChangeText={setMessage} editable={!busy} placeholder="What would you like us to know?" placeholderTextColor={styles.muted.color} />
              <Text style={styles.label}>Screenshot (optional)</Text>
              <View style={styles.row}>
                {Platform.OS === 'web' && <Pressable accessibilityRole="button" onPress={capture} disabled={busy} style={styles.button}><Text style={styles.text}>Capture screen</Text></Pressable>}
                <Pressable accessibilityRole="button" onPress={upload} disabled={busy} style={styles.button}><Text style={styles.text}>Upload image</Text></Pressable>
                {screenshot && <Pressable accessibilityRole="button" onPress={() => { setScreenshot(null); setSelection(null); }} disabled={busy} style={styles.button}><Text style={styles.text}>Remove image</Text></Pressable>}
              </View>
              {screenshot && <>
                <ScreenshotPreview screenshot={screenshot} selection={selection} onSelect={busy ? () => {} : setSelection} />
                {Platform.OS === 'web' && <Text style={styles.muted}>Drag to mark an area, or focus the preview and press Enter. Arrow keys move the mark; Shift + arrows resize it. The full screenshot will be sent.</Text>}
                {selection && <Pressable accessibilityRole="button" onPress={() => setSelection(null)} disabled={busy} style={styles.button}><Text style={styles.text}>Clear mark</Text></Pressable>}
              </>}
              <Text style={styles.muted}>Sent to the Eleplan team’s feedback channel. Check your image and remove any private information before sending.</Text>
              {!!error && <Text accessibilityRole="alert" accessibilityLiveRegion="assertive" style={styles.error}>{error}</Text>}
            </ScrollView>
            <Pressable accessibilityRole="button" accessibilityLabel="Send feedback" disabled={busy || (!message.trim() && !rating && !screenshot)} onPress={send} style={[styles.button, styles.primary, (busy || (!message.trim() && !rating && !screenshot)) && styles.disabled]}>
              {busy ? <ActivityIndicator color="#fff" /> : <Text style={styles.primaryText}>Send feedback</Text>}
            </Pressable>
          </>}
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}
